import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Search,
  Car as CarIcon,
  X,
  Trophy,
  XCircle,
  PlayCircle,
  Inbox,
  Archive,
  StickyNote,
  History,
  Trash2,
  MessageSquare,
  Send,
  ExternalLink,
  Download,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  setLeadStatus,
  bulkSetLeadStatus,
  addLeadNote,
  deleteLeadNote,
  getLeadDetails,
  sendQuickEmailToLead,
} from "@/lib/lead-pipeline.functions";
import { Highlight } from "@/components/admin/Highlight";
function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const s = String(value).replace(/"/g, '""');
  return `"${s}"`;
}

function downloadCsv(filename: string, rows: (string | number | null | undefined)[][]) {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
  // BOM for Excel UTF-8 compatibility
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}


type PipelineStatus = "new" | "in_progress" | "won" | "lost" | "archived";

const STAGES: { key: PipelineStatus; label: string; icon: typeof Inbox }[] = [
  { key: "new", label: "Indbakke", icon: Inbox },
  { key: "in_progress", label: "Behandles", icon: PlayCircle },
  { key: "won", label: "Vundet", icon: Trophy },
  { key: "lost", label: "Tabt", icon: XCircle },
  { key: "archived", label: "Arkiv", icon: Archive },
];

const STAGE_STYLES: Record<PipelineStatus, string> = {
  new: "bg-accent/15 text-accent ring-accent/30",
  in_progress: "bg-blue-500/15 text-blue-600 ring-blue-500/30",
  won: "bg-emerald-500/15 text-emerald-600 ring-emerald-500/30",
  lost: "bg-destructive/10 text-destructive ring-destructive/30",
  archived: "bg-muted text-muted-foreground ring-border",
};

const STAGE_LABELS: Record<PipelineStatus, string> = Object.fromEntries(
  STAGES.map((s) => [s.key, s.label]),
) as Record<PipelineStatus, string>;

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  stage: fallback(
    z.enum(["new", "in_progress", "won", "lost", "archived"]),
    "new",
  ).default("new"),
  selected: fallback(z.string().uuid().optional(), undefined).default(undefined),
});

type SearchState = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/beskeder")({
  validateSearch: zodValidator(searchSchema),
  component: AdminMessages,
});

function formatRelativeDa(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return new Date(iso).toLocaleDateString("da-DK");
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (minutes < 1) return "lige nu";
  if (minutes < 60) return `for ${minutes} min siden`;
  if (hours < 24) return `for ${hours} ${hours === 1 ? "time" : "timer"} siden`;
  if (days === 1) return "i går";
  if (days < 30) return `for ${days} dage siden`;
  return new Date(iso).toLocaleDateString("da-DK");
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("da-DK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type MessageRow = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  created_at: string;
  pipeline_status: PipelineStatus;
  is_read: boolean;
  car_id: string | null;
  sale_value_dkk: number | null;
  car?: {
    id: string;
    brand: string;
    model: string;
    variant: string | null;
    year: number;
    seo_slug: string | null;
    images: string[] | null;
  } | null;
};

function AdminMessages() {
  const queryClient = useQueryClient();
  const urlSearch: SearchState = Route.useSearch() as unknown as SearchState;
  const navigate = useNavigate({ from: "/admin/beskeder" });
  const setStageRef = useRef<(s: PipelineStatus) => void>(() => {});

  // Fetch ALL messages (no soft-delete filtering — nothing is hidden)
  const { data: allMessages, isLoading } = useQuery({
    queryKey: ["admin-pipeline-messages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_messages")
        .select(
          "id, name, email, phone, message, created_at, pipeline_status, is_read, car_id, sale_value_dkk, car:cars(id, brand, model, variant, year, seo_slug, images)",
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as MessageRow[];
    },
    refetchInterval: 15_000,
  });

  // Counts per stage (respects search)
  const { stageCounts, filtered } = useMemo(() => {
    const all = allMessages ?? [];
    const q = urlSearch.q.trim().toLowerCase();
    const matchesQuery = (m: MessageRow) => {
      if (!q) return true;
      const car = m.car;
      const carLabel = car
        ? `${car.brand} ${car.model}${car.variant ? ` ${car.variant}` : ""} ${car.year}`
        : "";
      return [m.name, m.email, m.phone, m.message, carLabel]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q));
    };
    const matched = all.filter(matchesQuery);
    const counts: Record<PipelineStatus, number> = {
      new: 0,
      in_progress: 0,
      won: 0,
      lost: 0,
      archived: 0,
    };
    matched.forEach((m) => {
      counts[m.pipeline_status] = (counts[m.pipeline_status] ?? 0) + 1;
    });
    return {
      stageCounts: counts,
      filtered: matched.filter((m) => m.pipeline_status === urlSearch.stage),
    };
  }, [allMessages, urlSearch.q, urlSearch.stage]);

  const setSearch = (q: string) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, q }), replace: true });
  };
  const setStage = (stage: PipelineStatus) => {
    setSelectedIds(new Set());
    navigate({
      search: (prev: SearchState) => ({ ...prev, stage, selected: undefined }),
      replace: true,
    });
  };
  setStageRef.current = setStage;

  const openDetail = (id: string) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, selected: id }), replace: true });
  };
  const closeDetail = () => {
    navigate({ search: (prev: SearchState) => ({ ...prev, selected: undefined }), replace: true });
  };

  // Mutations.
  // Authorization-headers tilføjes automatisk af den globale client-middleware
  // i src/start.ts — vi behøver ikke sende dem manuelt her.
  const setStatusFn = useServerFn(setLeadStatus);
  const bulkSetStatusFn = useServerFn(bulkSetLeadStatus);

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("contact_messages").update({ is_read: true }).eq("id", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pipeline-messages"] });
      queryClient.invalidateQueries({ queryKey: ["admin-unread-count"] });
    },
  });

  const setStatusMutation = useMutation({
    mutationFn: (vars: {
      id: string;
      status: PipelineStatus;
      saleValueDkk?: number;
    }) =>
      setStatusFn({
        data: {
          contactId: vars.id,
          status: vars.status,
          saleValueDkk: vars.saleValueDkk,
        },
      }),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin-pipeline-messages"] });
      queryClient.invalidateQueries({ queryKey: ["admin-unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["lead-details"] });
      if (vars.status === "won") {
        toast.success(
          `Markeret som vundet · Meta: ${res?.metaOk ? "✓" : "✗"} · GA4: ${res?.gaOk ? "✓" : "✗"}`,
        );
      } else {
        toast.success(`Status: ${STAGE_LABELS[vars.status]}`);
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke opdatere"),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: (vars: { ids: string[]; status: PipelineStatus }) =>
      bulkSetStatusFn({
        data: { contactIds: vars.ids, status: vars.status },
      }),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin-pipeline-messages"] });
      queryClient.invalidateQueries({ queryKey: ["admin-unread-count"] });
      const n = res?.updated ?? vars.ids.length;
      toast.success(`${n} ${n === 1 ? "lead" : "leads"} flyttet til ${STAGE_LABELS[vars.status]}`);
      setSelectedIds(new Set());
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke opdatere"),
  });

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleRowSelection = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const allSelected =
    filtered.length > 0 && filtered.every((m) => selectedIds.has(m.id));
  const someSelected = filtered.some((m) => selectedIds.has(m.id));
  const masterCheckState: boolean | "indeterminate" = allSelected
    ? true
    : someSelected
      ? "indeterminate"
      : false;
  const togglePageSelection = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) filtered.forEach((m) => next.add(m.id));
      else filtered.forEach((m) => next.delete(m.id));
      return next;
    });
  };

  // Won dialog
  const [wonDialog, setWonDialog] = useState<{ id: string; name: string } | null>(null);
  const [wonValue, setWonValue] = useState("");

  // Lost confirm dialog (irreversible-feeling action)
  const [lostDialog, setLostDialog] = useState<{ id: string; name: string } | null>(null);

  // Cmd/Ctrl+K → focus search
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const selectedCount = selectedIds.size;
  const selectedArray = Array.from(selectedIds);
  const selectedMessage = urlSearch.selected
    ? (allMessages ?? []).find((m) => m.id === urlSearch.selected) ?? null
    : null;

  // Eksportér de leads brugeren ser lige nu (respekterer stage + søgning).
  // Hvis der er valgt rækker, eksporteres kun de valgte; ellers eksporteres alle filtrerede.
  const handleExportCsv = () => {
    const source = selectedCount > 0
      ? filtered.filter((m) => selectedIds.has(m.id))
      : filtered;
    if (source.length === 0) {
      toast.error("Ingen leads at eksportere");
      return;
    }
    const header = [
      "Oprettet",
      "Navn",
      "Email",
      "Telefon",
      "Stage",
      "Læst",
      "Bil",
      "År",
      "Bil-ID",
      "Salgsbeløb (DKK)",
      "Besked",
    ];
    const rows: (string | number | null | undefined)[][] = [header];
    for (const m of source) {
      const car = m.car;
      const carLabel = car
        ? `${car.brand} ${car.model}${car.variant ? ` ${car.variant}` : ""}`
        : "";
      rows.push([
        formatDateTime(m.created_at),
        m.name,
        m.email,
        m.phone ?? "",
        STAGE_LABELS[m.pipeline_status],
        m.is_read ? "Ja" : "Nej",
        carLabel,
        car?.year ?? "",
        m.car_id ?? "",
        m.sale_value_dkk ?? "",
        (m.message ?? "").replace(/\r?\n/g, " "),
      ]);
    }
    const stamp = new Date().toISOString().slice(0, 10);
    const stagePart = urlSearch.stage;
    const qPart = urlSearch.q ? `-soeg-${urlSearch.q.replace(/[^a-z0-9]+/gi, "_").slice(0, 30)}` : "";
    const selPart = selectedCount > 0 ? "-valgte" : "";
    downloadCsv(`leads-${stagePart}${qPart}${selPart}-${stamp}.csv`, rows);
    toast.success(`Eksporterede ${source.length} ${source.length === 1 ? "lead" : "leads"}`);
  };


  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-foreground">Beskeder</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Lead-pipeline · {(allMessages ?? []).length} beskeder i alt
      </p>

      {/* Pipeline stage tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        {STAGES.map((stage) => {
          const Icon = stage.icon;
          const isActive = urlSearch.stage === stage.key;
          const count = stageCounts[stage.key] ?? 0;
          return (
            <button
              key={stage.key}
              type="button"
              onClick={() => setStage(stage.key)}
              className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                isActive
                  ? "border-accent bg-accent text-accent-foreground shadow-sm"
                  : "border-border bg-card text-muted-foreground hover:text-foreground hover:border-accent/40"
              }`}
            >
              <Icon size={16} />
              {stage.label}
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
                  isActive
                    ? "bg-accent-foreground/15 text-accent-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="mt-6">
        <div className="relative max-w-xl">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            ref={searchInputRef}
            value={urlSearch.q}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && urlSearch.q) {
                e.preventDefault();
                setSearch("");
              }
            }}
            placeholder="Søg navn, email, telefon, besked, bilmærke/model... (⌘/Ctrl+K)"
            className="pl-9 pr-9"
          />
          {urlSearch.q && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Ryd søgning"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {filtered.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-2.5">
          <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer select-none">
            <Checkbox
              checked={masterCheckState}
              onCheckedChange={(c) => togglePageSelection(c === true)}
              aria-label="Vælg alle"
            />
            {selectedCount > 0
              ? `${selectedCount} valgt`
              : `Vælg alle (${filtered.length})`}
          </label>
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Ryd valg
            </button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportCsv}
            className="ml-auto"
            title={
              selectedCount > 0
                ? `Eksportér ${selectedCount} valgte leads til CSV`
                : `Eksportér ${filtered.length} viste leads til CSV (stage: ${STAGE_LABELS[urlSearch.stage as PipelineStatus]}${urlSearch.q ? `, søgning: "${urlSearch.q}"` : ""})`
            }
          >
            <Download size={14} className="mr-1.5" />
            Eksportér CSV ({selectedCount > 0 ? selectedCount : filtered.length})
          </Button>
          {selectedCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {urlSearch.stage !== "in_progress" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    bulkStatusMutation.mutate({
                      ids: selectedArray,
                      status: "in_progress",
                    })
                  }
                  disabled={bulkStatusMutation.isPending}
                >
                  <PlayCircle size={14} className="mr-1.5" />
                  Behandl ({selectedCount})
                </Button>
              )}
              {urlSearch.stage !== "archived" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    bulkStatusMutation.mutate({
                      ids: selectedArray,
                      status: "archived",
                    })
                  }
                  disabled={bulkStatusMutation.isPending}
                >
                  <Archive size={14} className="mr-1.5" />
                  Arkivér ({selectedCount})
                </Button>
              )}
              {urlSearch.stage === "archived" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    bulkStatusMutation.mutate({ ids: selectedArray, status: "new" })
                  }
                  disabled={bulkStatusMutation.isPending}
                >
                  <Inbox size={14} className="mr-1.5" />
                  Genåbn ({selectedCount})
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Message list */}
      <div className="mt-4 space-y-3">
        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">Indlæser...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">
              {(() => {
                const stage: PipelineStatus = urlSearch.stage;
                return urlSearch.q
                  ? `Ingen beskeder matcher "${urlSearch.q}" i ${STAGE_LABELS[stage]}`
                  : `Ingen beskeder i ${STAGE_LABELS[stage]}`;
              })()}
            </p>
          </div>
        ) : (
          filtered.map((msg) => (
            <MessageCard
              key={msg.id}
              msg={msg}
              query={urlSearch.q}
              isSelected={selectedIds.has(msg.id)}
              onToggleSelect={(c) => toggleRowSelection(msg.id, c)}
              onOpen={() => {
                if (!msg.is_read) markReadMutation.mutate(msg.id);
                openDetail(msg.id);
              }}
              onSetStatus={(status) => {
                if (status === "won") {
                  setWonDialog({ id: msg.id, name: msg.name });
                  setWonValue(String(msg.sale_value_dkk ?? ""));
                } else if (status === "lost") {
                  setLostDialog({ id: msg.id, name: msg.name });
                } else {
                  setStatusMutation.mutate({ id: msg.id, status });
                }
              }}
            />
          ))
        )}
      </div>

      {/* Detail panel */}
      <Dialog open={!!selectedMessage} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedMessage && (
            <LeadDetailPanel
              message={selectedMessage}
              onSetStatus={(status) => {
                if (status === "won") {
                  setWonDialog({ id: selectedMessage.id, name: selectedMessage.name });
                  setWonValue(String(selectedMessage.sale_value_dkk ?? ""));
                } else if (status === "lost") {
                  setLostDialog({ id: selectedMessage.id, name: selectedMessage.name });
                } else {
                  setStatusMutation.mutate({ id: selectedMessage.id, status });
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Won dialog */}
      <Dialog
        open={!!wonDialog}
        onOpenChange={(o) => {
          if (!o) {
            setWonDialog(null);
            setWonValue("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Markér som vundet</DialogTitle>
            <DialogDescription>
              {wonDialog?.name} · Indtast salgsbeløb i DKK. Sender en offline Purchase-konvertering
              til Meta og GA4.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="mb-2 block text-sm font-medium">Salgsbeløb (DKK)</label>
            <Input
              type="number"
              inputMode="numeric"
              value={wonValue}
              onChange={(e) => setWonValue(e.target.value)}
              placeholder="f.eks. 250000"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWonDialog(null)}>
              Annullér
            </Button>
            <Button
              onClick={() => {
                const value = Number.parseInt(wonValue, 10);
                if (!Number.isFinite(value) || value < 0) {
                  toast.error("Indtast et gyldigt beløb");
                  return;
                }
                if (wonDialog) {
                  setStatusMutation.mutate({
                    id: wonDialog.id,
                    status: "won",
                    saleValueDkk: value,
                  });
                  setWonDialog(null);
                  setWonValue("");
                }
              }}
              disabled={setStatusMutation.isPending}
            >
              <Trophy size={14} className="mr-1.5" />
              Bekræft vundet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lost confirm */}
      <AlertDialog
        open={!!lostDialog}
        onOpenChange={(o) => !o && setLostDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Markér som tabt?</AlertDialogTitle>
            <AlertDialogDescription>
              {lostDialog?.name} flyttes til Tabt. Du kan altid genåbne leadet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annullér</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (lostDialog) {
                  setStatusMutation.mutate({ id: lostDialog.id, status: "lost" });
                  setLostDialog(null);
                }
              }}
            >
              Markér som tabt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Message card ────────────────────────────────────────────────────────────

function MessageCard({
  msg,
  query,
  isSelected,
  onToggleSelect,
  onOpen,
  onSetStatus,
}: {
  msg: MessageRow;
  query: string;
  isSelected: boolean;
  onToggleSelect: (checked: boolean) => void;
  onOpen: () => void;
  onSetStatus: (status: PipelineStatus) => void;
}) {
  const status = msg.pipeline_status;
  const statusStyle = STAGE_STYLES[status];
  const statusLabel = STAGE_LABELS[status];
  const car = msg.car;

  return (
    <div
      className={`rounded-xl bg-card p-5 shadow-sm ring-1 transition-all ${
        isSelected
          ? "ring-accent shadow-accent/10"
          : msg.is_read
            ? "ring-border"
            : "ring-accent/40 shadow-accent/5"
      }`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={(c) => onToggleSelect(c === true)}
          aria-label={`Vælg besked fra ${msg.name}`}
          className="mt-1"
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          onClick={onOpen}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-foreground">
              <Highlight text={msg.name} query={query} />
            </h3>
            {!msg.is_read && (
              <span className="rounded-md bg-accent px-2 py-0.5 text-xs font-semibold text-accent-foreground">
                Ulæst
              </span>
            )}
            <span className={`rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${statusStyle}`}>
              {statusLabel}
              {status === "won" && msg.sale_value_dkk
                ? ` · ${new Intl.NumberFormat("da-DK").format(msg.sale_value_dkk)} kr`
                : ""}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <a
              href={`mailto:${msg.email}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 hover:text-accent"
            >
              <Mail size={12} />
              <Highlight text={msg.email} query={query} />
            </a>
            {msg.phone && (
              <a
                href={`tel:${msg.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 hover:text-accent"
              >
                <Phone size={12} />
                <Highlight text={msg.phone} query={query} />
              </a>
            )}
            <span className="text-xs" title={formatDateTime(msg.created_at)}>
              {formatRelativeDa(msg.created_at)}
            </span>
          </div>
          <p className="mt-3 text-sm text-foreground/90 whitespace-pre-wrap line-clamp-3">
            <Highlight text={msg.message} query={query} />
          </p>
          {car && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-xs">
              <CarIcon size={12} className="text-accent" />
              <span className="font-medium">
                <Highlight
                  text={`${car.brand} ${car.model}${car.variant ? ` ${car.variant}` : ""} ${car.year}`}
                  query={query}
                />
              </span>
            </div>
          )}
        </button>
      </div>

      {/* Quick action bar */}
      <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
        {status === "new" && (
          <Button size="sm" variant="outline" onClick={() => onSetStatus("in_progress")}>
            <PlayCircle size={14} className="mr-1.5" />
            Start behandling
          </Button>
        )}
        {(status === "new" || status === "in_progress") && (
          <>
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400"
              onClick={() => onSetStatus("won")}
            >
              <Trophy size={14} className="mr-1.5" />
              Vundet
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
              onClick={() => onSetStatus("lost")}
            >
              <XCircle size={14} className="mr-1.5" />
              Tabt
            </Button>
          </>
        )}
        {(status === "won" || status === "lost") && (
          <Button size="sm" variant="ghost" onClick={() => onSetStatus("in_progress")}>
            <PlayCircle size={14} className="mr-1.5" />
            Genåbn
          </Button>
        )}
        {status !== "archived" && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto text-muted-foreground"
            onClick={() => onSetStatus("archived")}
          >
            <Archive size={14} className="mr-1.5" />
            Arkivér
          </Button>
        )}
        {status === "archived" && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto text-muted-foreground"
            onClick={() => onSetStatus("new")}
          >
            <Inbox size={14} className="mr-1.5" />
            Genåbn
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Detail panel ────────────────────────────────────────────────────────────

function LeadDetailPanel({
  message,
  onSetStatus,
}: {
  message: MessageRow;
  onSetStatus: (status: PipelineStatus) => void;
}) {
  const queryClient = useQueryClient();
  const getDetailsFn = useServerFn(getLeadDetails);
  const addNoteFn = useServerFn(addLeadNote);
  const deleteNoteFn = useServerFn(deleteLeadNote);
  const sendEmailFn = useServerFn(sendQuickEmailToLead);

  const [noteBody, setNoteBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const { data: details, isLoading } = useQuery({
    queryKey: ["lead-details", message.id],
    queryFn: () => getDetailsFn({ data: { messageId: message.id } }),
  });

  const addNoteMutation = useMutation({
    mutationFn: () =>
      addNoteFn({ data: { messageId: message.id, body: noteBody.trim() } }),
    onSuccess: () => {
      setNoteBody("");
      queryClient.invalidateQueries({ queryKey: ["lead-details", message.id] });
      toast.success("Note tilføjet");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke gemme note"),
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => deleteNoteFn({ data: { noteId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-details", message.id] });
      toast.success("Note slettet");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke slette note"),
  });

  const sendEmailMutation = useMutation({
    mutationFn: () =>
      sendEmailFn({
        data: {
          messageId: message.id,
          subject: emailSubject.trim(),
          body: emailBody.trim(),
        },
      }),
    onSuccess: () => {
      setEmailSubject("");
      setEmailBody("");
      queryClient.invalidateQueries({ queryKey: ["lead-details", message.id] });
      toast.success("Email sendt og logget");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Kunne ikke sende email"),
  });

  const car = message.car;
  const carLabel = car
    ? `${car.brand} ${car.model}${car.variant ? ` ${car.variant}` : ""} ${car.year}`
    : null;

  // Insert template into email body
  const applyTemplate = (subject: string, body: string) => {
    setEmailSubject(subject);
    setEmailBody(body);
  };

  const greetingName = message.name?.split(" ")[0] ?? "";
  const templates = [
    {
      label: "Tak – vender tilbage",
      subject: carLabel ? `Ang. ${carLabel}` : "Tak for din henvendelse",
      body: `Hej ${greetingName},\n\nTak for din henvendelse. Jeg vender tilbage til dig hurtigst muligt med svar på dine spørgsmål.\n\nVenlig hilsen`,
    },
    {
      label: "Bilen er stadig ledig",
      subject: carLabel ? `${carLabel} er stadig ledig` : "Bilen er stadig ledig",
      body: `Hej ${greetingName},\n\nTak for din interesse${carLabel ? ` i ${carLabel}` : ""}. Bilen er stadig ledig, og du er meget velkommen til at booke en fremvisning.\n\nGiv mig et par tider der passer dig, så finder vi en god løsning.\n\nVenlig hilsen`,
    },
    {
      label: "Tilbud om prøvetur",
      subject: "Prøvetur — find en tid",
      body: `Hej ${greetingName},\n\nVi tilbyder gerne en prøvetur${carLabel ? ` i ${carLabel}` : ""}. Skriv hvilken dag og tidspunkt der passer dig, så reserverer vi bilen til dig.\n\nVenlig hilsen`,
    },
  ];

  return (
    <div>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 flex-wrap">
          {message.name}
          <span
            className={`rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${STAGE_STYLES[message.pipeline_status]}`}
          >
            {STAGE_LABELS[message.pipeline_status]}
            {message.pipeline_status === "won" && message.sale_value_dkk
              ? ` · ${new Intl.NumberFormat("da-DK").format(message.sale_value_dkk)} kr`
              : ""}
          </span>
        </DialogTitle>
        <DialogDescription className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <a href={`mailto:${message.email}`} className="inline-flex items-center gap-1.5 hover:text-accent">
            <Mail size={12} />
            {message.email}
          </a>
          {message.phone && (
            <a href={`tel:${message.phone}`} className="inline-flex items-center gap-1.5 hover:text-accent">
              <Phone size={12} />
              {message.phone}
            </a>
          )}
          <span title={formatDateTime(message.created_at)}>{formatRelativeDa(message.created_at)}</span>
          <Link
            to="/admin/beskeder/$messageId"
            params={{ messageId: message.id }}
            className="ml-auto inline-flex items-center gap-1.5 text-accent hover:underline"
          >
            <ExternalLink size={12} />
            Åbn fuld side
          </Link>
        </DialogDescription>
      </DialogHeader>

      {/* Quick status actions */}
      <div className="mt-4 flex flex-wrap gap-2 border-y border-border py-3">
        {message.pipeline_status === "new" && (
          <Button size="sm" onClick={() => onSetStatus("in_progress")}>
            <PlayCircle size={14} className="mr-1.5" />
            Start behandling
          </Button>
        )}
        {(message.pipeline_status === "new" || message.pipeline_status === "in_progress") && (
          <>
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
              onClick={() => onSetStatus("won")}
            >
              <Trophy size={14} className="mr-1.5" />
              Vundet
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive"
              onClick={() => onSetStatus("lost")}
            >
              <XCircle size={14} className="mr-1.5" />
              Tabt
            </Button>
          </>
        )}
        {message.pipeline_status !== "archived" && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => onSetStatus("archived")}>
            <Archive size={14} className="mr-1.5" />
            Arkivér
          </Button>
        )}
        {message.pipeline_status === "archived" && (
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => onSetStatus("new")}>
            <Inbox size={14} className="mr-1.5" />
            Genåbn
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="conversation" className="mt-5">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="conversation" className="gap-1.5">
            <MessageSquare size={13} />
            <span className="hidden sm:inline">Samtale</span>
          </TabsTrigger>
          <TabsTrigger value="car" className="gap-1.5">
            <CarIcon size={13} />
            <span className="hidden sm:inline">Bil</span>
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-1.5">
            <StickyNote size={13} />
            <span className="hidden sm:inline">Noter</span>
            {(details?.notes.length ?? 0) > 0 && (
              <span className="rounded-full bg-accent/20 px-1.5 text-[10px] font-bold tabular-nums">
                {details!.notes.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-1.5">
            <Send size={13} />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
        </TabsList>

        {/* Samtale: original message + activity timeline */}
        <TabsContent value="conversation" className="mt-4 space-y-5">
          <div>
            <h4 className="mb-2 text-sm font-semibold text-foreground">Oprindelig besked</h4>
            <p className="whitespace-pre-wrap rounded-lg bg-muted/40 p-4 text-sm text-foreground">
              {message.message}
            </p>
          </div>

          <div>
            <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
              <History size={14} />
              Aktivitet
            </h4>
            <ol className="space-y-2 border-l-2 border-border pl-4">
              {details?.activity.map((event) => {
                const meta = (event.metadata ?? {}) as Record<string, unknown>;
                return (
                  <li key={event.id} className="relative">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-accent" />
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {describeActivity(event)}
                      </span>
                      {event.actor_email && ` · ${event.actor_email}`}
                      {" · "}
                      <span title={formatDateTime(event.created_at)}>
                        {formatRelativeDa(event.created_at)}
                      </span>
                    </div>
                    {event.event_type === "email_sent" && typeof meta.subject === "string" && (
                      <div className="mt-1 rounded-md bg-muted/40 p-2 text-xs">
                        <div className="font-medium text-foreground">{String(meta.subject)}</div>
                        {typeof meta.preview === "string" && (
                          <div className="mt-0.5 text-muted-foreground line-clamp-2">
                            {String(meta.preview)}
                          </div>
                        )}
                      </div>
                    )}
                    {event.event_type === "note_added" && typeof meta.preview === "string" && (
                      <p className="mt-1 text-xs text-muted-foreground italic line-clamp-2">
                        “{String(meta.preview)}”
                      </p>
                    )}
                  </li>
                );
              })}
              {(details?.activity.length ?? 0) === 0 && !isLoading && (
                <li className="text-xs text-muted-foreground italic">Ingen aktivitet endnu.</li>
              )}
            </ol>
          </div>
        </TabsContent>

        {/* Bil */}
        <TabsContent value="car" className="mt-4">
          {car ? (
            <div className="space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row">
                {car.images && car.images.length > 0 && (
                  <img
                    src={car.images[0]}
                    alt={`${car.brand} ${car.model}`}
                    className="h-40 w-full rounded-lg object-cover sm:w-56"
                    loading="lazy"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground">
                    {car.brand} {car.model}
                    {car.variant ? ` ${car.variant}` : ""}
                  </h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">Årgang {car.year}</p>

                  {car.seo_slug && (
                    <a
                      href={`/biler-til-salg/${car.seo_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                    >
                      <ExternalLink size={12} />
                      Åbn bilannonce
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-6 text-center">
              <CarIcon className="mx-auto mb-2 text-muted-foreground" size={24} />
              <p className="text-sm text-muted-foreground">
                Ingen bil tilknyttet denne henvendelse.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Noter */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <div className="space-y-2">
            <Textarea
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              placeholder="Skriv en intern note (kun synlig for admins)..."
              rows={3}
              maxLength={5000}
            />
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-muted-foreground">
                {noteBody.length}/5000
              </span>
              <Button
                size="sm"
                onClick={() => addNoteMutation.mutate()}
                disabled={!noteBody.trim() || addNoteMutation.isPending}
              >
                Tilføj note
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {isLoading && <p className="text-xs text-muted-foreground">Indlæser...</p>}
            {!isLoading && (details?.notes.length ?? 0) === 0 && (
              <p className="text-xs text-muted-foreground italic">Ingen noter endnu.</p>
            )}
            {details?.notes.map((note) => (
              <div key={note.id} className="rounded-lg border border-border bg-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {note.author_email ?? "Admin"}
                    </span>
                    {" · "}
                    <span title={formatDateTime(note.created_at)}>
                      {formatRelativeDa(note.created_at)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteNoteMutation.mutate(note.id)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Slet note"
                    title="Slet note"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{note.body}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Email — quick message */}
        <TabsContent value="email" className="mt-4 space-y-4">
          <div className="rounded-lg bg-muted/30 p-3 text-xs text-muted-foreground">
            Sender en transaktionel email til{" "}
            <span className="font-medium text-foreground">{message.email}</span> via
            AutoXChange-skabelonen og logger afsendelsen i aktivitetsloggen.
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Skabeloner
            </label>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => applyTemplate(t.subject, t.body)}
                  className="rounded-md border border-border bg-card px-2.5 py-1 text-xs hover:border-accent hover:text-accent"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="quick-email-subject" className="mb-1 block text-xs font-medium text-muted-foreground">
              Emne
            </label>
            <Input
              id="quick-email-subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder={carLabel ? `Ang. ${carLabel}` : "Emne på din besked"}
              maxLength={200}
            />
          </div>

          <div>
            <label htmlFor="quick-email-body" className="mb-1 block text-xs font-medium text-muted-foreground">
              Besked
            </label>
            <Textarea
              id="quick-email-body"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Skriv din besked til kunden…"
              rows={8}
              maxLength={10000}
            />
            <div className="mt-1 text-[11px] text-muted-foreground">
              {emailBody.length}/10000 · Brand-styling og afmeldingslink tilføjes
              automatisk.
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEmailSubject("");
                setEmailBody("");
              }}
              disabled={sendEmailMutation.isPending || (!emailSubject && !emailBody)}
            >
              Ryd
            </Button>
            <Button
              size="sm"
              onClick={() => sendEmailMutation.mutate()}
              disabled={
                sendEmailMutation.isPending ||
                !emailSubject.trim() ||
                !emailBody.trim()
              }
            >
              <Send size={14} className="mr-1.5" />
              {sendEmailMutation.isPending ? "Sender…" : "Send email"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function describeActivity(event: {
  event_type: string;
  from_status: string | null;
  to_status: string | null;
  metadata: unknown;
}): string {
  const stageLabel = (s: string | null): string =>
    s ? (STAGE_LABELS[s as PipelineStatus] ?? s) : "—";
  switch (event.event_type) {
    case "created":
      return "Lead oprettet";
    case "status_change":
      return `Status: ${stageLabel(event.from_status)} → ${stageLabel(event.to_status)}`;
    case "note_added":
      return "Note tilføjet";
    case "note_deleted":
      return "Note slettet";
    case "email_sent":
      return "Email sendt";
    default:
      return event.event_type;
  }
}
